/**
 * Fits the procedural face to a base model's own skull: finds where its eyes really are, then fills the eye sockets so
 * the face is a clean surface the editor can draw on anywhere.
 *
 *  - Depressions: each head vertex is compared with a heavily smoothed copy of the head (Laplacian, fixed border); the
 *    inward displacement along the normal marks sockets (and painted-eye relief).
 *  - Darkness: the painted eyes of the base texture are the darkest blobs of the face.
 *  - Eyes = the strongest (depression + darkness) blob on each side of the authored prior's centre line; their midpoint
 *    and spacing become the face frame, their spread the default eye size. The nose tip is the most forward point on the
 *    centre line below the eyes; the mouth sits under it.
 *  - Filling: a robust quadratic fit replaces the folded socket depth while preserving x/y, UVs and the skull outline.
 *    A local nose relief keeps cat muzzles; species with long snouts retain their original muzzle and teeth entirely.
 */
import type { V3 } from './rig';

export interface FaceFit {
  pos: Float32Array;
  indices: Uint32Array;
  /** Newly closed holes start here and need a continuous local UV projection. */
  patchIndexStart: number;
  /** Detached thin whisker ribbons: omit their exaggerated inverted hull. */
  whiskers: Uint8Array;
  frame: { center: V3; scale: number };
  eyeSize: number;
  /** Face-space offsets for the shape parameters (see face.ts layout: nose −0.36, mouth −0.62). */
  noseY: number;
  mouthY: number;
  eraseY: number;
  eyeL: V3; eyeR: V3; nose: V3;
}

export function fitFace(
  pos: Float32Array, indices: Uint32Array, owner: Uint8Array, headBones: Set<number>,
  prior: { center: V3; scale: number }, darkness: (vertex: number) => number,
  options: { preserveMuzzle?: boolean; softenMuzzleRim?: boolean; preserveBrowRidge?: boolean } = {},
): FaceFit {
  const vc = pos.length / 3;
  // weld (UV seams duplicate positions)
  const ids = new Map<string, number>(); const group = new Int32Array(vc); const first: number[] = [];
  for (let i = 0; i < vc; i++) {
    const k = `${Math.round(pos[i * 3] * 2e4)},${Math.round(pos[i * 3 + 1] * 2e4)},${Math.round(pos[i * 3 + 2] * 2e4)}`;
    let g = ids.get(k);
    if (g === undefined) { g = first.length; ids.set(k, g); first.push(i); }
    group[i] = g;
  }
  const n = first.length;
  const P = new Float32Array(n * 3), N = new Float32Array(n * 3), head = new Uint8Array(n), dark = new Float32Array(n), cnt = new Float32Array(n);
  first.forEach((src, g) => { P.set(pos.subarray(src * 3, src * 3 + 3), g * 3); });
  for (let i = 0; i < vc; i++) { const g = group[i]; if (headBones.has(owner[i])) head[g] = 1; dark[g] += darkness(i); cnt[g]++; }
  for (let g = 0; g < n; g++) dark[g] /= Math.max(1, cnt[g]);
  const nbr: Set<number>[] = Array.from({ length: n }, () => new Set<number>());
  for (let t = 0; t < indices.length; t += 3) {
    const a = group[indices[t]], b = group[indices[t + 1]], c = group[indices[t + 2]];
    const ux = P[b * 3] - P[a * 3], uy = P[b * 3 + 1] - P[a * 3 + 1], uz = P[b * 3 + 2] - P[a * 3 + 2];
    const vx = P[c * 3] - P[a * 3], vy = P[c * 3 + 1] - P[a * 3 + 1], vz = P[c * 3 + 2] - P[a * 3 + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const v of [a, b, c]) { N[v * 3] += nx; N[v * 3 + 1] += ny; N[v * 3 + 2] += nz; }
    nbr[a].add(b); nbr[a].add(c); nbr[b].add(a); nbr[b].add(c); nbr[c].add(a); nbr[c].add(b);
  }
  for (let g = 0; g < n; g++) { const l = Math.hypot(N[g * 3], N[g * 3 + 1], N[g * 3 + 2]) || 1; N[g * 3] /= l; N[g * 3 + 1] /= l; N[g * 3 + 2] /= l; }
  // Meshy stores whiskers and some painted dots as separate thin pieces. Do not
  // stretch those pieces when rebuilding the skull behind them.
  const componentSize = new Uint32Array(n), components: number[][] = [];
  for (let g = 0; g < n; g++) {
    if (componentSize[g]) continue;
    const part = [g]; componentSize[g] = 1;
    for (let q = 0; q < part.length; q++) for (const j of nbr[part[q]]) if (!componentSize[j]) { componentSize[j] = 1; part.push(j); }
    for (const j of part) componentSize[j] = part.length;
    components.push(part);
  }
  // smoothed head (non-head vertices fixed)
  let S = new Float32Array(P);
  for (let it = 0; it < 24; it++) {
    const next = new Float32Array(S);
    for (let g = 0; g < n; g++) {
      if (!head[g] || !nbr[g].size) continue;
      for (let k = 0; k < 3; k++) { let s = 0; for (const j of nbr[g]) s += S[j * 3 + k]; next[g * 3 + k] = S[g * 3 + k] + 0.5 * (s / nbr[g].size - S[g * 3 + k]); }
    }
    S = next;
  }
  const disp = new Float32Array(n);
  for (let g = 0; g < n; g++) disp[g] = (P[g * 3] - S[g * 3]) * N[g * 3] + (P[g * 3 + 1] - S[g * 3 + 1]) * N[g * 3 + 1] + (P[g * 3 + 2] - S[g * 3 + 2]) * N[g * 3 + 2];
  // the prior window (authored rough frame)
  const pc = prior.center, ps = prior.scale;
  const uv = (g: number) => [(P[g * 3] - pc[0]) / ps, (P[g * 3 + 1] - pc[1]) / ps] as const;
  const inWindow = (g: number) => { if (!head[g] || N[g * 3 + 2] < 0.2) return false; const [u, v] = uv(g); return Math.abs(u) > 0.12 && Math.abs(u) < 1.3 && v > -0.6 && v < 0.6; };
  // only the front shell of the face: sockets on the sides/top of a big round skull are not eyes
  let zFront = -Infinity;
  for (let g = 0; g < n; g++) if (inWindow(g)) zFront = Math.max(zFront, P[g * 3 + 2]);
  const front = (g: number) => P[g * 3 + 2] > zFront - 0.9 * ps && N[g * 3 + 2] > 0.4;
  let maxDep = 1e-6, maxDark = 1e-6;
  for (let g = 0; g < n; g++) if (inWindow(g) && front(g)) { maxDep = Math.max(maxDep, -disp[g]); maxDark = Math.max(maxDark, dark[g]); }
  const score = new Float32Array(n);
  // sockets weigh more than paint (forehead stripes are dark too), and eyes are expected near (±0.5, 0) of the prior frame
  for (let g = 0; g < n; g++) if (inWindow(g) && front(g)) {
    const [u, v] = uv(g);
    // the authored frame is only a rough hint: a broad prior; the painted pupils (the darkest paint) dominate stripes
    const prior = Math.exp(-(((Math.abs(u) - 0.55) / 0.5) ** 2 + (v / 0.75) ** 2));
    const dk = Math.pow(dark[g] / maxDark, 3);
    score[g] = (0.45 * Math.max(0, -disp[g]) / maxDep + 0.55 * dk) * (0.4 + 0.6 * prior);
  }
  const eyeOf = (side: number): { c: V3; r: number } => {
    const cand: number[] = [];
    for (let g = 0; g < n; g++) if (score[g] > 0 && Math.sign(uv(g)[0]) === side) cand.push(g);
    cand.sort((a, b) => score[b] - score[a]);
    const top = cand.slice(0, Math.max(4, Math.round(cand.length * 0.07)));
    let wx = 0, wy = 0, wz = 0, ws = 0;
    for (const g of top) { const w = score[g] * score[g]; wx += P[g * 3] * w; wy += P[g * 3 + 1] * w; wz += S[g * 3 + 2] * w; ws += w; }
    const c: V3 = ws ? [wx / ws, wy / ws, wz / ws] : [pc[0] + side * ps * 0.5, pc[1], pc[2]];
    let r2 = 0;
    for (const g of top) { const w = score[g] * score[g]; r2 += ((P[g * 3] - c[0]) ** 2 + (P[g * 3 + 1] - c[1]) ** 2) * w; }
    return { c, r: Math.sqrt(r2 / Math.max(ws, 1e-9)) };
  };
  const L = eyeOf(1), R = eyeOf(-1);
  // keep the pair level and symmetric about its midpoint
  const mid: V3 = [(L.c[0] + R.c[0]) / 2, (L.c[1] + R.c[1]) / 2, Math.max(L.c[2], R.c[2])];
  const half = Math.max(ps * 0.3, Math.abs(L.c[0] - R.c[0]) / 2);
  const scale = half * 2;
  const eyeL: V3 = [mid[0] + half, mid[1], L.c[2]], eyeR: V3 = [mid[0] - half, mid[1], R.c[2]];
  // nose tip: most forward point on the centre line below the eyes
  let nose: V3 = [mid[0], mid[1] - scale * 0.45, mid[2]];
  let bestZ = -Infinity;
  for (let g = 0; g < n; g++) {
    if (!head[g] || N[g * 3 + 2] < 0.3) continue;
    const u = (P[g * 3] - mid[0]) / scale, v = (P[g * 3 + 1] - mid[1]) / scale;
    if (Math.abs(u) < 0.22 && v < -0.15 && v > -1.3 && P[g * 3 + 2] > bestZ) { bestZ = P[g * 3 + 2]; nose = [P[g * 3], P[g * 3 + 1], P[g * 3 + 2]]; }
  }
  const noseV = (nose[1] - mid[1]) / scale;
  // Reconstruct a single-valued front surface. Imported eyes contain folded
  // triangles; pushing each one along its own normal makes those folds worse.
  // Fit the surrounding skull instead, and move only in depth so the silhouette
  // and texture coordinates do not tear. Robust fitting excludes eye sockets.
  const samples: { q: number[]; z: number; w: number }[] = [];
  const terms = (u: number, v: number) => [1, u, v, u * u, u * v, v * v];
  for (let g = 0; g < n; g++) {
    if (!head[g] || N[g * 3 + 2] < 0.3 || P[g * 3 + 2] < mid[2] - scale * 0.5) continue;
    const u = (P[g * 3] - mid[0]) / scale, v = (P[g * 3 + 1] - mid[1]) / scale;
    if (Math.abs(u) > 1.45 || v < -1.35 || v > 0.95) continue;
    if (Math.abs(Math.abs(u) - 0.5) < 0.38 && Math.abs(v) < 0.3) continue;
    samples.push({ q: terms(u, v), z: (P[g * 3 + 2] - mid[2]) / scale, w: 1 });
  }
  let coeff = [0.3, 0, 0, -0.15, 0, -0.08];
  const predict = (q: number[]) => q.reduce((s, x, i) => s + x * coeff[i], 0);
  for (let pass = 0; pass < 3 && samples.length > 12; pass++) {
    const a = Array.from({ length: 6 }, (_, r) => Array.from({ length: 7 }, (_, c) => c === r ? 1e-5 : 0));
    for (const s of samples) for (let r = 0; r < 6; r++) {
      for (let c = 0; c < 6; c++) a[r][c] += s.q[r] * s.q[c] * s.w;
      a[r][6] += s.q[r] * s.z * s.w;
    }
    for (let r = 0; r < 6; r++) {
      let pivot = r; for (let k = r + 1; k < 6; k++) if (Math.abs(a[k][r]) > Math.abs(a[pivot][r])) pivot = k;
      [a[r], a[pivot]] = [a[pivot], a[r]];
      const div = a[r][r] || 1e-8; for (let c = r; c < 7; c++) a[r][c] /= div;
      for (let k = 0; k < 6; k++) if (k !== r) { const f = a[k][r]; for (let c = r; c < 7; c++) a[k][c] -= f * a[r][c]; }
    }
    coeff = a.map(r => r[6]);
    for (const s of samples) { const residual = Math.abs(s.z - predict(s.q)); s.w = residual < 0.09 ? 1 : 0.09 / residual; }
  }
  const noseU = (nose[0] - mid[0]) / scale;
  const muzzleLift = Math.max(0, (nose[2] - mid[2]) / scale - predict(terms(noseU, noseV)));
  const filled = new Float32Array(P);
  for (let g = 0; g < n; g++) {
    if (!head[g] || componentSize[g] < 150 || P[g * 3 + 2] < mid[2] - scale * 0.5) continue;
    const u = (P[g * 3] - mid[0]) / scale, v = (P[g * 3 + 1] - mid[1]) / scale;
    const k = Math.hypot(u / 1.45, (v + 0.15) / 1.15);
    const fade = Math.max(0, Math.min(1, (1.2 - k) / 0.3));
    const socketDistance = options.preserveMuzzle
      ? Math.hypot((Math.abs(u) - 0.5) / 0.67, (v - 0.17) / 0.85)
      : Math.hypot((Math.abs(u) - 0.5) / 0.58, (v - 0.1) / 0.48);
    const repairDistance = socketDistance;
    const local = Math.max(0, Math.min(1, (1.2 - repairDistance) / 0.28));
    const w = fade * fade * (3 - 2 * fade) * local * local * (3 - 2 * local);
    if (!w) continue;
    const muzzle = muzzleLift * Math.exp(-(((u - noseU) / 0.4) ** 2) - ((v - noseV) / 0.45) ** 2);
    let surface = mid[2] + scale * (predict(terms(u, v)) + muzzle);
    if (options.preserveBrowRidge) {
      // A slender forehead has little depth above its sockets. A broad fitted
      // surface can recess that ridge into a notch, leaving a projecting plate
      // in profile. Keep its native depth while still rebuilding the eyes below.
      const upper = Math.max(0, Math.min(1, (v - 0.05) / 0.25));
      surface = Math.max(surface, P[g * 3 + 2] - scale * (0.6 - upper * 0.54));
    }
    filled[g * 3 + 2] = P[g * 3 + 2] + (surface - P[g * 3 + 2]) * w;
  }
  const whiskerGroups = new Uint8Array(n);
  // Imported whiskers are often broad, kinked ribbons. Fit their centre curves
  // and reduce only the ribbon thickness; endpoints and the species' sweep stay.
  for (const part of components) {
    if (part.length < 8 || part.length >= 150) continue;
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const g of part) for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], P[g * 3 + k]); hi[k] = Math.max(hi[k], P[g * 3 + k]); }
    const span = hi.map((x, k) => x - lo[k]), axis = span.indexOf(Math.max(...span));
    const u = ((lo[0] + hi[0]) * .5 - mid[0]) / scale, v = ((lo[1] + hi[1]) * .5 - mid[1]) / scale;
    if (span[axis] < scale * .7 || Math.abs(u) < .35 || Math.abs(u) > 2.5 || v < -1.8 || v > -.1 || lo[2] < mid[2] - scale * .4) continue;
    for (const g of part) whiskerGroups[g] = 1;
    for (let k = 0; k < 3; k++) {
      if (k === axis) continue;
      const a = Array.from({ length: 3 }, (_, r) => Array.from({ length: 4 }, (_, c) => c === r ? 1e-6 : 0));
      for (const g of part) {
        const t = (P[g * 3 + axis] - lo[axis]) / span[axis] * 2 - 1, q = [1, t, t * t];
        for (let r = 0; r < 3; r++) { for (let c = 0; c < 3; c++) a[r][c] += q[r] * q[c]; a[r][3] += q[r] * P[g * 3 + k]; }
      }
      for (let r = 0; r < 3; r++) { const div = a[r][r]; for (let c = r; c < 4; c++) a[r][c] /= div; for (let j = 0; j < 3; j++) if (j !== r) { const f = a[j][r]; for (let c = r; c < 4; c++) a[j][c] -= f * a[r][c]; } }
      for (const g of part) { const t = (P[g * 3 + axis] - lo[axis]) / span[axis] * 2 - 1, curve = a[0][3] + a[1][3] * t + a[2][3] * t * t; filled[g * 3 + k] = curve + (P[g * 3 + k] - curve) * .22; }
    }
  }
  const out = new Float32Array(pos);
  for (let i = 0; i < vc; i++) out.set(filled.subarray(group[i] * 3, group[i] * 3 + 3), i * 3);
  // Folded socket walls have become coplanar after reconstruction. They are
  // internal surfaces, not silhouettes: retain only their forward skin so the
  // outline pass cannot put the old socket/whisker cavities over the new face.
  const inCleanFace = (i: number): boolean => {
    const g = group[i], u = (P[g * 3] - mid[0]) / scale, v = (P[g * 3 + 1] - mid[1]) / scale;
    const repaired = options.preserveMuzzle
      ? Math.hypot((Math.abs(u) - 0.5) / 0.67, (v - 0.17) / 0.85)
      : Math.hypot((Math.abs(u) - 0.5) / 0.58, (v - 0.1) / 0.48);
    return !!head[g] && P[g * 3 + 2] >= mid[2] - scale * 0.5
      && Math.hypot(u / 1.45, (v + 0.15) / 1.15) < 1.1
      && repaired < 1.05;
  };
  const cleanIndices: number[] = [];
  const scraps = new Uint8Array(n);
  for (const part of components) {
    if (part.length >= 100) continue;
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const g of part) for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], P[g * 3 + k]); hi[k] = Math.max(hi[k], P[g * 3 + k]); }
    const u = ((lo[0] + hi[0]) * 0.5 - mid[0]) / scale, v = ((lo[1] + hi[1]) * 0.5 - mid[1]) / scale;
    // Small floating dot/splinter geometry inside the face is superseded by
    // procedural ink. Long whiskers and the hare's incisors stay untouched.
    const extent = Math.max(...hi.map((x, k) => x - lo[k]));
    const surfaceScrap = extent < scale * .7 && v > -1.6 && v < .7 && lo[2] > mid[2] - scale * .4;
    const floatingWhiskerRoot = extent < scale * .25 && v > -1.8 && v < -.25 && lo[2] > mid[2] - scale * .95;
    if (Math.abs(u) < 1.5 && (surfaceScrap || floatingWhiskerRoot)) {
      for (const g of part) scraps[g] = 1;
    }
  }
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t], b = indices[t + 1], c = indices[t + 2];
    if (scraps[group[a]] && scraps[group[b]] && scraps[group[c]]) continue;
    const nz = (out[b * 3] - out[a * 3]) * (out[c * 3 + 1] - out[a * 3 + 1]) - (out[b * 3 + 1] - out[a * 3 + 1]) * (out[c * 3] - out[a * 3]);
    if (nz <= 1e-10 && inCleanFace(a) && inCleanFace(b) && inCleanFace(c)) {
      // Socket folds can share vertices with several surface fans. Dropping a
      // reversed triangle leaves a tiny non-manifold hole at the eye corner.
      // Once projected onto the fitted skin, it is safe to orient it forwards.
      if (nz < -1e-10) cleanIndices.push(a, c, b);
      continue;
    }
    cleanIndices.push(a, b, c);
  }
  const originalSurfaceCount = cleanIndices.length;
  // Close tiny open loops left by imported eye and whisker sockets. A mask cannot fix
  // those holes: the camera sees the dark inside of the skull through them.
  const edges = new Map<string, { a: number; b: number; count: number }>();
  for (let t = 0; t < cleanIndices.length; t += 3) for (let k = 0; k < 3; k++) {
    const a = group[cleanIndices[t + k]], b = group[cleanIndices[t + (k + 1) % 3]];
    if (a === b) continue;
    const key = a < b ? `${a},${b}` : `${b},${a}`, edge = edges.get(key);
    if (edge) edge.count++; else edges.set(key, { a, b, count: 1 });
  }
  const boundary = new Map<number, number[]>();
  for (const { a, b, count } of edges.values()) if (count === 1) {
    boundary.set(a, [...(boundary.get(a) ?? []), b]); boundary.set(b, [...(boundary.get(b) ?? []), a]);
  }
  const visited = new Set<string>(), capped = new Set<string>();
  for (const [start, neighbors] of boundary) for (const neighbor of neighbors) {
    if (visited.has(`${start},${neighbor}`)) continue;
    const loop = [start]; let prev = start, current = neighbor;
    while (current !== start && loop.length <= 24) {
      const edge = `${prev},${current}`;
      if (visited.has(edge) || loop.includes(current)) break;
      visited.add(edge); loop.push(current);
      const candidates = boundary.get(current)?.filter(v => v !== prev) ?? [];
      if (!candidates.length) break;
      // Some imported eye corners join three boundary fans. Follow their planar
      // half-edges instead of requiring every vertex to have exactly two edges.
      const back = Math.atan2(filled[prev * 3 + 1] - filled[current * 3 + 1], filled[prev * 3] - filled[current * 3]);
      const turn = (v: number) => (Math.atan2(filled[v * 3 + 1] - filled[current * 3 + 1], filled[v * 3] - filled[current * 3]) - back + Math.PI * 2) % (Math.PI * 2);
      candidates.sort((a, b) => turn(a) - turn(b));
      prev = current; current = candidates[0];
    }
    if (current !== start || loop.length < 3 || loop.length > 24) continue;
    visited.add(`${prev},${start}`);
    const signature = [...loop].sort((a, b) => a - b).join(',');
    if (capped.has(signature)) continue;
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    let onFace = true;
    for (const g of loop) {
      const u = (P[g * 3] - mid[0]) / scale, v = (P[g * 3 + 1] - mid[1]) / scale;
      if (componentSize[g] < 150 || Math.abs(u) > 1.35 || v < -1.5 || v > .6 || filled[g * 3 + 2] < mid[2] - scale * .1) onFace = false;
      for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], filled[g * 3 + k]); hi[k] = Math.max(hi[k], filled[g * 3 + k]); }
    }
    if (!onFace || Math.max(...hi.map((v, k) => v - lo[k])) > scale * .45) continue;
    let area = 0;
    for (let k = 0; k < loop.length; k++) { const a = loop[k], b = loop[(k + 1) % loop.length]; area += filled[a * 3] * filled[b * 3 + 1] - filled[b * 3] * filled[a * 3 + 1]; }
    if (Math.abs(area) < 1e-10) continue;
    capped.add(signature);
    if (area < 0) loop.reverse();
    for (let k = 1; k < loop.length - 1; k++) cleanIndices.push(first[loop[0]], first[loop[k]], first[loop[k + 1]]);
  }
  // A few authored eye seams terminate at T-junctions instead of closed loops.
  // Stitch only their very short gaps on the reconstructed skin; these cannot
  // include a nostril, mouth, silhouette or any unrepaired part of the model.
  const eyeEdge = [...boundary.keys()].filter(g => inCleanFace(first[g]) && filled[g * 3 + 2] > mid[2] - scale * .1);
  const stitched = new Set<string>();
  const cross = (a: number, b: number, c: number) => (filled[b * 3] - filled[a * 3]) * (filled[c * 3 + 1] - filled[a * 3 + 1]) - (filled[b * 3 + 1] - filled[a * 3 + 1]) * (filled[c * 3] - filled[a * 3]);
  for (const center of eyeEdge) {
    const near = eyeEdge.filter(g => Math.hypot(filled[g * 3] - filled[center * 3], filled[g * 3 + 1] - filled[center * 3 + 1]) < scale * .045 && Math.abs(filled[g * 3 + 2] - filled[center * 3 + 2]) < scale * .025);
    if (near.length < 3) continue;
    near.sort((a, b) => filled[a * 3] - filled[b * 3] || filled[a * 3 + 1] - filled[b * 3 + 1]);
    const lower: number[] = [], upper: number[] = [];
    for (const g of near) { while (lower.length > 1 && cross(lower.at(-2)!, lower.at(-1)!, g) <= 0) lower.pop(); lower.push(g); }
    for (const g of near.reverse()) { while (upper.length > 1 && cross(upper.at(-2)!, upper.at(-1)!, g) <= 0) upper.pop(); upper.push(g); }
    const hull = [...lower.slice(0, -1), ...upper.slice(0, -1)];
    const signature = [...hull].sort((a, b) => a - b).join(',');
    if (hull.length < 3 || stitched.has(signature)) continue;
    stitched.add(signature);
    for (let k = 1; k < hull.length - 1; k++) cleanIndices.push(first[hull[0]], first[hull[k]], first[hull[k + 1]]);
  }
  const eyeSize = Math.min(0.4, Math.max(0.24, (Math.max(L.r, R.r) * 1.9) / scale));
  const patchedGroups = new Uint8Array(n);
  for (let t = originalSurfaceCount; t < cleanIndices.length; t++) patchedGroups[group[cleanIndices[t]]] = 1;
  const rimInfluence = new Float32Array(n);
  const muzzleRim = [...patchedGroups.keys()].filter(g => patchedGroups[g] && (P[g * 3 + 1] - mid[1]) / scale < -.4);
  // Whisker-hole rims can themselves be rolled strips. Fit the new patch to
  // the surrounding muzzle rather than retaining that raised, torn edge.
  for (let g = 0; g < n; g++) {
    const v = (P[g * 3 + 1] - mid[1]) / scale;
    if ((!patchedGroups[g] && !options.softenMuzzleRim) || !head[g] || componentSize[g] < 150 || v > -.4) continue;
    let influence = patchedGroups[g] ? 1 : 0;
    if (!influence) {
      let nearest = Infinity;
      for (const rim of muzzleRim) {
        if (Math.abs(P[g * 3 + 2] - P[rim * 3 + 2]) > scale * .15) continue;
        nearest = Math.min(nearest, Math.hypot(P[g * 3] - P[rim * 3], P[g * 3 + 1] - P[rim * 3 + 1]));
      }
      const fade = Math.max(0, 1 - nearest / (scale * .1));
      influence = fade * fade * (3 - 2 * fade);
    }
    if (!influence) continue;
    rimInfluence[g] = influence;
    const a = Array.from({ length: 3 }, (_, r) => Array.from({ length: 4 }, (_, c) => c === r ? 1e-6 : 0));
    let samples = 0;
    for (let j = 0; j < n; j++) {
      if (patchedGroups[j] || !head[j] || componentSize[j] < 150) continue;
      const x = (P[j * 3] - P[g * 3]) / scale, y = (P[j * 3 + 1] - P[g * 3 + 1]) / scale;
      if (Math.hypot(x, y) > .24 || Math.abs(P[j * 3 + 2] - P[g * 3 + 2]) > scale * .25) continue;
      const q = [1, x, y], w = 1 / (.02 + x * x + y * y); samples++;
      for (let r = 0; r < 3; r++) { for (let c = 0; c < 3; c++) a[r][c] += q[r] * q[c] * w; a[r][3] += q[r] * P[j * 3 + 2] * w; }
    }
    if (samples < 6) continue;
    for (let r = 0; r < 3; r++) { const div = a[r][r]; for (let c = r; c < 4; c++) a[r][c] /= div; for (let j = 0; j < 3; j++) if (j !== r) { const f = a[j][r]; for (let c = r; c < 4; c++) a[j][c] -= f * a[r][c]; } }
    filled[g * 3 + 2] += (a[0][3] - filled[g * 3 + 2]) * influence;
  }
  // A repaired whisker root can contain a second, reversed atlas fan over the
  // front skin. It is an internal fold, not part of the muzzle silhouette.
  // Remove only reversed triangles wholly inside the tiny repaired rim.
  let surfaceIndices: number[] = cleanIndices, repairedSurfaceCount = originalSurfaceCount;
  if (options.softenMuzzleRim) {
    surfaceIndices = []; repairedSurfaceCount = 0;
    for (let t = 0; t < cleanIndices.length; t += 3) {
      const [a, b, c] = cleanIndices.slice(t, t + 3).map(i => group[i]);
      const facing = (filled[b * 3] - filled[a * 3]) * (filled[c * 3 + 1] - filled[a * 3 + 1]) - (filled[b * 3 + 1] - filled[a * 3 + 1]) * (filled[c * 3] - filled[a * 3]);
      if (t < originalSurfaceCount && Math.min(rimInfluence[a], rimInfluence[b], rimInfluence[c]) > .05 && facing < -1e-10) continue;
      surfaceIndices.push(...cleanIndices.slice(t, t + 3));
      if (t < originalSurfaceCount) repairedSurfaceCount += 3;
    }
  }
  for (let i = 0; i < vc; i++) out.set(filled.subarray(group[i] * 3, group[i] * 3 + 3), i * 3);
  const noseParam = noseV + 0.36 + 0.06;
  const mouthParam = (noseV - 0.24) + 0.62;
  return {
    pos: out, indices: new Uint32Array(surfaceIndices), patchIndexStart: repairedSurfaceCount, whiskers: Uint8Array.from(group, g => whiskerGroups[g]), frame: { center: mid, scale }, eyeSize,
    noseY: Math.max(-0.6, Math.min(0.15, noseParam)), mouthY: Math.max(-0.6, Math.min(0.15, mouthParam)),
    eraseY: Math.min(0, Math.max(-0.4, (noseV + 0.45) * 0.6)),
    eyeL, eyeR, nose,
  };
}

/** Paint closed whisker holes from one coherent neighbouring UV island. The
 * second projection fades back into the original atlas over nearby vertices;
 * seam vertices at the same position receive identical weights. */
export function textureFacePatches(fit: FaceFit, sourceUv: Float32Array, feather = true): { pos: Float32Array; uv: Float32Array; repair: Float32Array; indices: Uint32Array; source: Uint32Array } {
  const count = fit.pos.length / 3, p = fit.pos, scale = fit.frame.scale;
  const triangleCenter = (t: number) => [0, 1, 2].map(k => (p[fit.indices[t] * 3 + k] + p[fit.indices[t + 1] * 3 + k] + p[fit.indices[t + 2] * 3 + k]) / 3);
  const distance = (a: number[], b: number[]) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const clusters: { center: number[]; vertices: Set<number>; radius: number }[] = [];
  for (let t = fit.patchIndexStart; t < fit.indices.length; t += 3) {
    const center = triangleCenter(t), group = clusters.find(c => distance(c.center, center) < scale * .08);
    const vertices = [...fit.indices.subarray(t, t + 3)];
    if (!group) clusters.push({ center, vertices: new Set(vertices), radius: 0 });
    else vertices.forEach(v => group.vertices.add(v));
  }
  for (const cluster of clusters) {
    cluster.center = [0, 1, 2].map(k => [...cluster.vertices].reduce((sum, v) => sum + p[v * 3 + k], 0) / cluster.vertices.size);
    cluster.radius = Math.max(...[...cluster.vertices].map(v => distance([...p.subarray(v * 3, v * 3 + 3)], cluster.center)));
  }
  const templates: { center: number[]; origin: number[]; uv: number[]; dx: number[]; dy: number[] }[] = [];
  for (let s = 0; s < fit.patchIndexStart; s += 3) {
    const center = triangleCenter(s);
    if (feather && clusters.some(c => distance(center, c.center) < c.radius + scale * .12)) continue;
    const a = fit.indices[s], b = fit.indices[s + 1], c = fit.indices[s + 2];
    const bx = p[b * 3] - p[a * 3], by = p[b * 3 + 1] - p[a * 3 + 1], cx = p[c * 3] - p[a * 3], cy = p[c * 3 + 1] - p[a * 3 + 1];
    const det = bx * cy - by * cx;
    if (det < scale ** 2 * .002) continue;
    const bu = sourceUv[b * 2] - sourceUv[a * 2], bv = sourceUv[b * 2 + 1] - sourceUv[a * 2 + 1], cu = sourceUv[c * 2] - sourceUv[a * 2], cv = sourceUv[c * 2 + 1] - sourceUv[a * 2 + 1];
    if (Math.max(Math.hypot(bu, bv), Math.hypot(cu, cv), Math.hypot(bu - cu, bv - cv)) > .12) continue;
    templates.push({ center, origin: center.slice(0, 2), uv: [(sourceUv[a * 2] + sourceUv[b * 2] + sourceUv[c * 2]) / 3, (sourceUv[a * 2 + 1] + sourceUv[b * 2 + 1] + sourceUv[c * 2 + 1]) / 3], dx: [(bu * cy - cu * by) / det, (bv * cy - cv * by) / det], dy: [(cu * bx - bu * cx) / det, (cv * bx - bv * cx) / det] });
  }
  const patchTemplates = clusters.map(cluster => {
    let best = Infinity, template: typeof templates[number] | null = null;
    for (const candidate of templates) {
      const dist = distance(candidate.center, cluster.center);
      if (dist < best) { best = dist; template = candidate; }
    }
    return template;
  });
  if (feather) {
    const category = (c: typeof clusters[number]) => `${Math.sign(c.center[0] - fit.frame.center[0])}:${c.center[1] < fit.frame.center[1] - scale * .4}`;
    for (let c = 0; c < clusters.length; c++) patchTemplates[c] = patchTemplates[clusters.findIndex(other => category(other) === category(clusters[c]))];
  }
  const vertexWeight = new Float32Array(count);
  for (let i = 0; i < count; i++) for (const cluster of clusters) {
    const edge = distance([...p.subarray(i * 3, i * 3 + 3)], cluster.center) - cluster.radius;
    const fade = Math.max(0, Math.min(1, (edge - scale * .055) / (scale * .16)));
    vertexWeight[i] = Math.max(vertexWeight[i], 1 - fade * fade * (3 - 2 * fade));
  }
  const targets: number[] = [];
  for (let t = 0; t < fit.indices.length; t += 3) if (feather ? [...fit.indices.subarray(t, t + 3)].some(i => vertexWeight[i] > 0) : t >= fit.patchIndexStart) targets.push(t);
  const finalCount = count + targets.length * 3, pos = new Float32Array(finalCount * 3), uv = new Float32Array(finalCount * 2), repair = new Float32Array(finalCount * 3), source = new Uint32Array(finalCount), indices = new Uint32Array(fit.indices);
  pos.set(p); uv.set(sourceUv);
  for (let i = 0; i < count; i++) source[i] = i;
  // A triangle must never interpolate UV coordinates belonging to different
  // atlas islands, even when one vertex has zero repair opacity.
  targets.forEach((t, q) => {
    const center = triangleCenter(t);
    let nearest = 0;
    for (let c = 1; c < clusters.length; c++) if (distance(center, clusters[c].center) < distance(center, clusters[nearest].center)) nearest = c;
    const template = patchTemplates[nearest];
    for (let k = 0; k < 3; k++) {
      const old = fit.indices[t + k], next = count + q * 3 + k;
      source[next] = old; indices[t + k] = next;
      pos.set(p.subarray(old * 3, old * 3 + 3), next * 3); uv.set(sourceUv.subarray(old * 2, old * 2 + 2), next * 2);
      if (!template) continue;
      const x = p[old * 3] - template.origin[0], y = p[old * 3 + 1] - template.origin[1];
      for (let d = 0; d < 2; d++) repair[next * 3 + d] = Math.max(0, Math.min(1, template.uv[d] + (feather ? Math.max(-.003, Math.min(.003, (x * template.dx[d] + y * template.dy[d]) * .12)) : x * template.dx[d] + y * template.dy[d])));
      repair[next * 3 + 2] = feather ? vertexWeight[old] : 1;
    }
  });
  return { pos, uv, repair, indices, source };
}
